import * as _ from 'underscore';

import * as validatorsDispatcher from './validatorsDispatcher';
import * as Globalization from './Globalization';

const URL_REGEXP = /^http(?:s)?\:\/\//;
const COLOR_HEX_REGEXP = /^\#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/; //check HEX like: #aa33ff
const COLOR_RGB_REGEXP = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/; //matches RGB, like rgb(255, 0,0)
const COLOR_RGBA_REGEXP = /^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*(\d*(?:\.\d+)?)\)$/; //matches RGB, like rgba(255,0,0,0.15)

//const REG_EXP_EMAIL = /^[-0-9a-zA-Z.+_]+@[-0-9a-zA-Z.+_]+\.[a-zA-Z]{2,4}$/;
// see RFC 5322
const REG_EXP_EMAIL =
    /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i;
//var REG_EXP_HOUR_MINUTE_SECOND = /(00|[0-9]|0[0-9]|1[0-6]):([0-5][0-9]):([0-5][0-9])$/;
const REG_EXP_HOUR_MINUTE_SECOND = /(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/;
const REG_CURRENCY_3_SYMBOLS = /^[-A-Z]{3}$/;

const MIXED_LETTER_CASE_REGEXP = /^(?=.*[a-z])(?=.*[A-Z]).*$/;
const NUMBERS_AND_LETTERS_REGEXP = /^(?=.*[a-zA-Z])(?=.*[0-9]).*$/;

export const validators = {
    notNull: function (value) {
        if (value == null) {
            return ['Cannot be null'];
        }
    },

    shouldMatch: function (name) {
        return function (value, values) {
            if (typeof value === values[name]) {
                return;
            }

            return ['Must match'];
        };
    },

    color: {
        shouldBe: function (value) {
            if (
                typeof value === 'string' &&
                (COLOR_HEX_REGEXP.test(value) ||
                    value === 'transparent' ||
                    COLOR_RGB_REGEXP.test(value) ||
                    COLOR_RGBA_REGEXP.test(value))
            ) {
                return;
            }

            return ['Wrong color value rgba or hex'];
        }
    },

    password: {
        oldPasswordNotEmpty: (password: any) => {
            if (typeof password !== 'string' || password.length === 0) {
                return ['Old password should be specified'];
            }

            return undefined;
        },

        shouldBe: (value) => {
            if (value == null || typeof value === 'string') {
                return;
            }

            return ['Password must be a text string'];
        },

        notEmpty: (password: any) => {
            if (typeof password !== 'string' || password.length === 0) {
                return ['Password should be specified'];
            }

            return undefined;
        },

        length: (minLength: number) => {
            return (password: any) => {
                if (
                    minLength != null &&
                    minLength > 0 &&
                    (typeof password !== 'string' || password.length < minLength)
                ) {
                    return [`Password should be at least ${minLength} characters long`];
                }

                return undefined;
            };
        },

        containsNumbersAndLetters: (password: any) => {
            if (typeof password !== 'string' || !NUMBERS_AND_LETTERS_REGEXP.test(password)) {
                return ['Password must have numbers and letters'];
            }

            return undefined;
        },

        mixedLetterCase: (password: any) => {
            if (typeof password !== 'string' || !MIXED_LETTER_CASE_REGEXP.test(password)) {
                return ['Password must have mixed letter case'];
            }

            return undefined;
        },

        shouldBeConfirmed: (password: string, confirmPassword: string) => {
            if (password !== confirmPassword) {
                return ['Passwords should match'];
            }

            return undefined;
        }
    },

    string: {
        mixedLetterCase: function (str) {
            if (typeof str !== 'string' || !MIXED_LETTER_CASE_REGEXP.test(str)) {
                return ['Must contain uppercase and lowercase letters'];
            }
        },

        containsNumbersAndLetters: function (str) {
            if (typeof str !== 'string' || !NUMBERS_AND_LETTERS_REGEXP.test(str)) {
                return ['Must contain numbers and letters'];
            }
        },

        length: function (min: number, max: number) {
            const _max = max === -1 ? Number.POSITIVE_INFINITY : max;

            return function (value) {
                if (typeof value !== 'string') {
                    return;
                }

                const errors = [];

                if (min != null && value.length < min) {
                    errors.push(`Value must have at least ${min} symbols`);
                }

                if (_max != null && value.length > _max) {
                    errors.push(`Value must not be longer than ${_max} symbols`);
                }

                if (errors.length > 0) {
                    return errors;
                }
            };
        },

        shouldBe: (value) => {
            if (value == null || typeof value === 'string') {
                return;
            }

            return ['Field value must be a text string'];
        },

        notEmpty: (value) => {
            if (value !== '') {
                return;
            }

            return ['This field cannot be empty'];
        },

        notAllBlanks: (value) => {
            if (value != null) {
                if (value.trim() !== '') {
                    return;
                }
            }

            return ['This field cannot be empty or contain pure blanks'];
        },

        url: function (value) {
            if (URL_REGEXP.test(value)) {
                return;
            }

            return ['URL is not valid'];
        },

        hourMinuteSecond: function (value) {
            if (REG_EXP_HOUR_MINUTE_SECOND.test(value)) {
                return;
            }

            return ['Invalid input string. Use one like: "23:30:59"'];
        },

        currency3symbols: function (value) {
            if (!REG_CURRENCY_3_SYMBOLS.test(value)) {
                return ['Use 3 capital symbols like: "USD"'];
            }

            return Globalization.checkCurrency(value);
        },

        email: function (value) {
            //Checks for exactly one email in the text.
            //REG_EXP_EMAIL allows any text after email + ' '.
            if (value != null && value.indexOf(' ') === -1 && REG_EXP_EMAIL.test(value)) {
                return;
            }

            return ['Email is not valid'];
        },

        emailOrEmpty: function (value) {
            if (value == null || value === '') {
                return;
            }

            return validators.string.email(value);
        }
    },

    date: {
        dateOrNull: function (value) {
            if (value === null || value === '') {
                return;
            }

            return validators.date.shouldBe(value);
        },

        shouldBe: (value) => {
            if (value != null && value !== '') {
                if (
                    !isNaN(Date.parse(value)) ||
                    (typeof value === 'string' && value.indexOf(':') > 0) //Check for 'time'.
                ) {
                    return;
                }
            }

            return ['Value must be a date'];
        }
    },

    number: {
        shouldBe: (value: number) => {
            if (typeof value === 'number' && !isNaN(value)) {
                return;
            }

            return ['Field value must be a number'];
        },

        positive: (value: number) => {
            if (typeof value !== 'number' || isNaN(value)) {
                return;
            }

            if (value > 0) {
                return;
            }

            return ['Field value must be a positive number'];
        },

        //LL:
        not_negative: function (value) {
            if (typeof value !== 'number' || isNaN(value)) {
                return;
            }

            if (value >= 0) {
                return;
            }

            return ['Field value must be a not negative number'];
        },

        integer: function (value) {
            if (typeof value !== 'number' || isNaN(value)) {
                return;
            }

            if (Math.floor(value) - value === 0) {
                return;
            }

            return ['Field value must be an integer number'];
        },

        range: function (from, to) {
            return function (value) {
                if (typeof value !== 'number' || isNaN(value)) {
                    return;
                }

                if (value < from || value > to) {
                    return ['Field value must be in range from ' + from + ' to ' + to];
                }
            };
        },

        length: function (minLength: number, maxLength: number) {
            const min = minLength > 0 ? Math.pow(10, minLength) - 1 : 0;
            const max = maxLength > 0 ? Math.pow(10, maxLength) - 1 : Number.POSITIVE_INFINITY;

            return function (value) {
                if (typeof value !== 'number' || isNaN(value)) {
                    return;
                }

                const errors = [];

                if (value < min) {
                    errors.push(`Value must have at least ${minLength} digits`);
                }

                if (value > max) {
                    errors.push(`Value must not be longer than ${maxLength} digits`);
                }

                if (errors.length > 0) {
                    return errors;
                }
            };
        }
    }
};

validatorsDispatcher.registerModule('main', validators);
